import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { IoSaveOutline, IoClose, IoBarcodeOutline, IoCameraOutline } from "react-icons/io5";
import api from "@/api";
import { useI18n, ALL_STORES, STORE_LABELS } from "@/i18n";
import { ExpiryDateInput } from "@/components/ExpiryDateInput";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { useAuth } from "@/auth";
import { tapSuccess, tapError } from "@/utils/haptics";

export default function ExpiryFormPage() {
  const { expiryId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, isDirettore } = useAuth();
  const isEdit = Boolean(expiryId);
  const [form, setForm] = useState({
    store_id: isDirettore ? user?.storeId : ALL_STORES[0],
    product_name: "",
    article_code: "",
    barcode: "",
    quantity: 1,
    expiry_date: "",
    destination: "",
    notes: "",
    cost: "",
    price: "",
    image_file_id: "",
    image_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [shelfOcrBusy, setShelfOcrBusy] = useState(false);
  const shelfFileRef = useRef(null);

  const handleShelfTagPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setShelfOcrBusy(true);
    const tid = toast.loading("Leggo il cartellino…");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/ocr/shelf-tag", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filled = [];
      setForm((f) => {
        const next = { ...f };
        if (data.name && !f.product_name) { next.product_name = data.name; filled.push("nome"); }
        if (data.article_code && !f.article_code) { next.article_code = data.article_code; filled.push("codice articolo"); }
        if (data.barcode && !f.barcode) { next.barcode = data.barcode; filled.push("EAN"); }
        if (data.price != null && (f.price === "" || f.price == null)) { next.price = data.price; filled.push("prezzo"); }
        if (data.cost != null && (f.cost === "" || f.cost == null)) { next.cost = data.cost; filled.push("costo"); }
        return next;
      });
      toast.dismiss(tid);
      if (filled.length) { tapSuccess(); toast.success(`Compilato dal cartellino: ${filled.join(", ")}`); }
      else toast.info("Nessun campo leggibile — riprova con foto più nitida");
    } catch (err) {
      tapError();
      toast.dismiss(tid);
      toast.error(err?.response?.data?.detail || "OCR fallito");
    } finally {
      setShelfOcrBusy(false);
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/expiries/${expiryId}`);
        setForm({
          store_id: data.store_id,
          product_name: data.product_name || "",
          article_code: data.article_code || "",
          barcode: data.barcode || "",
          quantity: data.quantity ?? 1,
          expiry_date: data.expiry_date || "",
          destination: data.destination || "",
          notes: data.notes || "",
          cost: data.cost ?? "",
          price: data.price ?? "",
          image_file_id: data.image_file_id || "",
          image_url: data.image_url || "",
        });
      } catch {
        toast.error("Scadenza non trovata");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    })();
  }, [expiryId, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /** Prefill matching product fields from EAN or article code (Conad listino + cache). */
  const prefillFromLookup = async (code) => {
    const trimmed = (code || "").toString().trim();
    if (!trimmed) return;
    try {
      const { data } = await api.get(`/products/lookup/${encodeURIComponent(trimmed)}`);
      if (!data || !data.name) return;
      setForm((f) => ({
        ...f,
        product_name: f.product_name || data.name || "",
        article_code: f.article_code || data.article_code || "",
        barcode: f.barcode || data.barcode || "",
        cost: (f.cost === "" || f.cost == null) && data.cost != null ? data.cost : f.cost,
        price: (f.price === "" || f.price == null) && data.price != null ? data.price : f.price,
      }));
      toast.success(`Compilato dal listino: ${data.name}`);
    } catch { /* silent */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    const qty = Number(form.quantity);
    if (!Number.isFinite(qty) || qty < 1) {
      toast.error("La quantità è obbligatoria (min. 1)");
      return;
    }
    if (!form.expiry_date) {
      toast.error("La data di scadenza è obbligatoria");
      return;
    }
    if (!form.destination.trim()) {
      toast.error(t("destination_required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: qty,
        cost: form.cost === "" ? null : Number(form.cost),
        price: form.price === "" ? null : Number(form.price),
      };
      if (isEdit) {
        await api.put(`/expiries/${expiryId}`, payload);
        tapSuccess();
        toast.success("Scadenza aggiornata");
      } else {
        await api.post("/expiries", payload);
        tapSuccess();
        toast.success("Scadenza salvata");
      }
      navigate("/expiries");
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mobile-frame">
        <div className="tf-header-simple">
          <button className="tf-close-btn" onClick={() => navigate(-1)}><IoClose size={22} /></button>
          <div className="tf-header-title-center">…</div>
          <div style={{ minWidth: 44 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-frame" data-testid="expiry-form-page">
      <div className="tf-header-simple">
        <button data-testid="ef-close" className="tf-close-btn" onClick={() => navigate("/expiries")}>
          <IoClose size={22} />
        </button>
        <div className="tf-header-title-center">{isEdit ? t("edit_article") : t("add_expiry")}</div>
        <div style={{ minWidth: 44 }} />
      </div>

      <form onSubmit={submit} className="tf-content">
        {!isEdit && (
          <>
            <input
              ref={shelfFileRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              data-testid="ef-shelf-tag-file"
              onChange={handleShelfTagPhoto}
            />
            <button
              type="button"
              data-testid="ef-shelf-tag-btn"
              className="tf-btn-primary"
              onClick={() => shelfFileRef.current?.click()}
              disabled={shelfOcrBusy}
              style={{ width: "100%", marginBottom: 14, justifyContent: "center" }}
            >
              <IoCameraOutline size={20} />
              {shelfOcrBusy ? "Lettura in corso…" : "Fotografa il cartellino dello scaffale"}
            </button>
          </>
        )}

        <div className="tf-field">
          <label className="tf-label">Punto vendita</label>
          {isDirettore ? (
            <input
              data-testid="ef-store-locked"
              className="tf-input"
              value={t(STORE_LABELS[form.store_id] || "")}
              readOnly
            />
          ) : (
            <select
              data-testid="ef-store"
              className="tf-select"
              value={form.store_id}
              onChange={(e) => set("store_id", e.target.value)}
            >
              {ALL_STORES.map((s) => <option key={s} value={s}>{t(STORE_LABELS[s])}</option>)}
            </select>
          )}
        </div>

        <div className="tf-field">
          <label className="tf-label">{t("product_name")} *</label>
          <input data-testid="ef-name" className="tf-input" value={form.product_name} onChange={(e) => set("product_name", e.target.value)} />
        </div>

        <div className="tf-field">
          <label className="tf-label">Codice articolo</label>
          <input
            data-testid="ef-article-code"
            className="tf-input"
            value={form.article_code}
            onChange={(e) => set("article_code", e.target.value)}
            onBlur={(e) => prefillFromLookup(e.target.value)}
            placeholder="Es. 0096731.001"
          />
        </div>

        <div className="tf-field">
          <label className="tf-label">Codice EAN</label>
          <div className="tf-input-wrap">
            <input
              data-testid="ef-barcode"
              className="tf-input tf-input-with-icon-right"
              value={form.barcode}
              onChange={(e) => set("barcode", e.target.value)}
              onBlur={(e) => prefillFromLookup(e.target.value)}
              inputMode="numeric"
              placeholder="8001120450081"
              style={{ paddingRight: 60 }}
            />
            <button
              type="button"
              data-testid="ef-barcode-scan"
              onClick={() => setScannerOpen(true)}
              className="tf-icon-btn red"
              style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", width: 40, height: 40 }}
              aria-label={t("scan_barcode")}
            >
              <IoBarcodeOutline size={20} />
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="tf-field">
            <label className="tf-label">{t("quantity")}</label>
            <input
              data-testid="ef-quantity"
              className="tf-input"
              type="number"
              min={1}
              required
              value={form.quantity}
              onChange={(e) => set("quantity", e.target.value)}
              onFocus={(e) => { if (String(form.quantity) === "0" || String(form.quantity) === "1") { set("quantity", ""); e.target.select(); } }}
              inputMode="numeric"
            />
          </div>
          <div className="tf-field">
            <label className="tf-label">{t("expiry_date")} *</label>
            <ExpiryDateInput
              value={form.expiry_date}
              onChange={(v) => set("expiry_date", v)}
              testIdPrefix="ef-date"
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="tf-field">
            <label className="tf-label">{t("cost")} (€)</label>
            <input data-testid="ef-cost" className="tf-input" type="number" step="0.01" value={form.cost} onChange={(e) => set("cost", e.target.value)} inputMode="decimal" />
          </div>
          <div className="tf-field">
            <label className="tf-label">{t("price")} (€)</label>
            <input data-testid="ef-price" className="tf-input" type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} inputMode="decimal" />
          </div>
        </div>

        <div className="tf-field">
          <label className="tf-label">{t("destination")} *</label>
          <input
            data-testid="ef-destination"
            className="tf-input"
            value={form.destination}
            onChange={(e) => set("destination", e.target.value)}
            placeholder={t("destination_placeholder")}
            required
          />
        </div>

        <button type="submit" data-testid="ef-save" className="tf-btn-primary" disabled={saving}>
          <IoSaveOutline size={18} />
          {saving ? t("saving") : t("save")}
        </button>
      </form>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={(code) => { set("barcode", code); setScannerOpen(false); prefillFromLookup(code); }}
      />
    </div>
  );
}
